import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { downloadRevealAnimation } from '@/components/sections/animations'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type DownloadProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    items: {
      title: string
      text: string
      file: {
        url: string
      }
    }[]
  }>

const DownloadItem = ({
  title,
  text,
  file
}: {
  title: string
  text: string
  file: {
    url: string
  }
}) => {
  const { strings } = useStore()
  return (
    <Grid
      className="relative mb-spacing-sm gap-spacing-sm border-b border-[black]/20 pb-spacing-sm md:gap-0"
      data-motion-item
    >
      <Link
        href={file?.url}
        target="_blank"
        className="absolute inset-0 z-10 text-[0px]"
      >
        {strings?.download}
      </Link>
      <div className="col-span-2 md:col-span-1 lg:col-span-2">
        <div
          className={cn(
            'flex items-center justify-center rounded bg-surface-primary-light text-text-primary-invert-light dark:bg-surface-primary-dark dark:text-text-primary-invert-dark',
            'size-[27px] md:size-[76px]'
          )}
        >
          <SVGIcon
            symbol="pdf"
            className={cn('h-[9px] w-[8px] md:h-[27px] md:w-[24px]')}
            aria-hidden="true"
          />
        </div>
      </div>
      <div className="col-span-2 flex flex-col gap-spacing-sm md:col-span-4 lg:col-span-8">
        <Typography variant="h5" paragraph>
          {title}
        </Typography>
        <Typography variant="small" paragraph>
          {text}
        </Typography>
      </div>
      <div className="col-span-2 flex h-[50px] justify-start md:col-span-1 md:h-auto md:justify-end lg:col-span-2">
        <CtaButton
          href={file?.url}
          target={'_blank'}
          variant="secondary"
          typography="small"
          className="condensed absolute z-20 place-self-start whitespace-nowrap"
        >
          {strings?.download_pdf || 'Download PDF'}
        </CtaButton>
      </div>
    </Grid>
  )
}

const Download = ({ items, ...props }: DownloadProps) => {
  return (
    <Motion animation={downloadRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Spacer variant="xl" />
          <Container>
            {(items &&
              items?.map((item, index) => {
                return (
                  <DownloadItem
                    key={index}
                    title={item.title}
                    text={item.text}
                    file={item.file}
                  />
                )
              })) ||
              null}
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )
}

export default Download
